// src/components/EMS/EMS/Reports&Analytics/ResourceStatusHistory.jsx
import React, { useState, useEffect } from 'react';
import { format, parseISO } from 'date-fns';
import Pagination from '../../Tools/Pagination';
import axiosInstance from '../../../../api';
import html2canvas from 'html2canvas';
import { jsPDF } from 'jspdf';

const ResourceStatusHistory = ({ onExport }) => {
  // STATE
  const [resourceStatusHistory, setResourceStatusHistory] = useState([]);
  const [loadingResourceStatus, setLoadingResourceStatus] = useState(false);
  const [filtersResourceStatus, setFiltersResourceStatus] = useState({
    status: '',
    datetime: '',
    resource_name: '',
  });
  const [pageResourceStatus, setPageResourceStatus] = useState(1);
  const limitResourceStatus = 10;

  // DATE FORMAT
  const dateFormat = sessionStorage.getItem('date_format') || 'DD/MM/YYYY';
  const timeFormat = sessionStorage.getItem('time_format') || '24';

  const formatDateTime = (dateString) => {
     if (!dateString) return 'N/A';
   
     try {
       const dateFormat = sessionStorage.getItem('date_format') || 'DD/MM/YYYY';
       const timeFormat = sessionStorage.getItem('time_format') || '24';
   
       // Map your backend formats to date-fns
       const dateFormatMap = {
         'DD/MM/YYYY': 'dd/MM/yyyy',
         'MM/DD/YYYY': 'MM/dd/yyyy',
         'YYYY-MM-DD': 'yyyy-MM-dd',
       };
   
       const timeFormatString = timeFormat === '12' ? 'hh:mm:ss a' : 'HH:mm:ss';
       const formatString = `${dateFormatMap[dateFormat] || 'dd/MM/yyyy'} ${timeFormatString}`;
   
       let utcDate;
   
       if (dateString.includes('T')) {
         // ISO format: 2025-10-27T16:27:19.000000Z
         utcDate = new Date(dateString.endsWith('Z') ? dateString : dateString + 'Z');
       } else {
         // MySQL format: 2025-10-27 16:27:19
         utcDate = new Date(dateString.replace(' ', 'T') + 'Z'); // Force UTC
       }
   
       // Extract UTC parts manually to avoid local timezone shift
       const formatted = format(
         new Date(
           utcDate.getUTCFullYear(),
           utcDate.getUTCMonth(),
           utcDate.getUTCDate(),
           utcDate.getUTCHours(),
           utcDate.getUTCMinutes(),
           utcDate.getUTCSeconds()
         ),
         formatString
       );
   
       return formatted;
     } catch (err) {
       console.error('Error formatting alarm time:', err, dateString);
       return 'Invalid Date';
     }
   };

  // FETCH DATA
  const fetchResourceStatusHistory = async () => {
    try {
      setLoadingResourceStatus(true);
      const { data } = await axiosInstance.get('/ems/resource-status-history', {
        params: {
          user_id: 68,
          status: filtersResourceStatus.status,
          datetime: filtersResourceStatus.datetime,
          resource_name: filtersResourceStatus.resource_name,
        },
      });

      const apiData = Array.isArray(data) ? data : data?.data ?? [];
      setResourceStatusHistory(apiData);
    } catch (error) {
      console.error('Error fetching resource status history:', error);
    } finally {
      setLoadingResourceStatus(false);
    }
  };

  useEffect(() => {
    fetchResourceStatusHistory();
  }, [
    filtersResourceStatus.status,
    filtersResourceStatus.datetime,
    filtersResourceStatus.resource_name,
  ]);

  // FILTER HANDLERS
  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFiltersResourceStatus((prev) => ({ ...prev, [name]: value }));
    setPageResourceStatus(1);
  };

  const handleClearFilters = () => {
    setFiltersResourceStatus({ status: '', datetime: '', resource_name: '' });
    setPageResourceStatus(1);
  };

  // PAGINATION 
  const totalItems = resourceStatusHistory.length;
  const paginatedData = resourceStatusHistory.slice(
    (pageResourceStatus - 1) * limitResourceStatus,
    pageResourceStatus * limitResourceStatus
  );

  //PDF EXPORT (ALL PAGES)
  const exportToPDF = async () => {
    if (!resourceStatusHistory.length) {
      alert('No data to export.');
      return;
    }

    const container = document.createElement('div');
    container.style.position = 'absolute';
    container.style.left = '-9999px';
    container.style.top = '-9999px';
    container.style.width = '210mm';
    container.style.padding = '15mm';
    container.style.backgroundColor = '#fff';
    container.style.fontFamily = 'Helvetica, sans-serif';
    container.style.fontSize = '10pt';
    document.body.appendChild(container);

    const { createRoot } = await import('react-dom/client');
    const root = createRoot(container);

    const fullTable = (
      <div>
        <table style={{ width: '100%', borderCollapse: 'collapse', marginTop: '10px', fontSize: '10pt' }}>
          <thead>
            <tr>
              {['Resource Name', 'Previous to New Status', 'Date Changed', 'Changed By'].map((h) => (
                <th
                  key={h}
                  style={{
                    border: '1px solid #dee2e6',
                    padding: '10px 8px',
                    backgroundColor: '#f8f9fa',
                    textAlign: 'left',
                    fontWeight: 'bold',
                  }}
                >
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {resourceStatusHistory.map((item) => (
              <tr key={item.id}>
                <td style={{ border: '1px solid #dee2e6', padding: '8px', whiteSpace: 'normal', wordBreak: 'break-word' }}>
                  {item.resource_name ?? 'N/A'}
                </td>
                <td style={{ border: '1px solid #dee2e6', padding: '8px', whiteSpace: 'normal', wordBreak: 'break-word' }}>
                  {item.previous_and_new_status ?? 'N/A'}
                </td>
                <td style={{ border: '1px solid #dee2e6', padding: '8px', whiteSpace: 'normal', wordBreak: 'break-word' }}>
                  {item.created_at ? formatDateTime(item.created_at) : 'N/A'}
                </td>
                <td style={{ border: '1px solid #dee2e6', padding: '8px', whiteSpace: 'normal', wordBreak: 'break-word' }}>
                  {`${item.user?.first_name ?? ''} ${item.user?.last_name ?? ''}`.trim() || 'N/A'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );

    root.render(fullTable);
    await new Promise((resolve) => setTimeout(resolve, 150));

    const canvas = await html2canvas(container, {
      scale: 2,
      useCORS: true,
      backgroundColor: '#ffffff',
      logging: false,
    });

    root.unmount();
    document.body.removeChild(container);

    const imgData = canvas.toDataURL('image/png');
    const pdf = new jsPDF('p', 'mm', 'a4');
    const pageWidth = pdf.internal.pageSize.getWidth();
    const pageHeight = pdf.internal.pageSize.getHeight();
    const imgWidth = pageWidth - 20;
    const imgHeight = (canvas.height * imgWidth) / canvas.width;

    let heightLeft = imgHeight;
    let position = 25;

    pdf.setFontSize(16);
    pdf.text('Resource Status History', pageWidth / 2, 15, { align: 'center' });
    pdf.setFontSize(10);
    pdf.text(`Generated: ${formatDateTime(new Date().toISOString())}`, 10, 20);

    pdf.addImage(imgData, 'PNG', 10, position, imgWidth, imgHeight);
    heightLeft -= pageHeight - position - 10;

    while (heightLeft > 0) {
      pdf.addPage();
      position = 15;
      pdf.addImage(imgData, 'PNG', 10, position, imgWidth, imgHeight);
      heightLeft -= pageHeight - 20;
    } 
    pdf.save('resource_status_history.pdf');
  };

  // RENDER
  return (
    <div className="card reports-analytics w-100 position-relative overflow-hidden">
      <div className="card-body p-4">
        <h5 className="card-title fw-semibold mb-0 lh-sm">Resource Status History</h5>
        <p className="card-subtitle mb-0 mt-1">
          Track all changes in the status of resources over time.
        </p>

        {/* Filters & Export */}
        <div className="filter-panel mt-2 mb-4">
          <form className="col-md-9 d-flex flex-wrap filter-left-panel">
            <div className="form-group col-md-4 col-sm-6 col-12 p-2">
              <label>Date</label>
              <input
                type="date"
                name="datetime"
                value={filtersResourceStatus.datetime}
                onChange={handleFilterChange}
                className="form-control"
              />
            </div>

            <div className="form-group col-md-4 col-sm-6 col-12 p-2">
              <label>Status</label>
              <select
                name="status"
                value={filtersResourceStatus.status}
                onChange={handleFilterChange}
                className="form-control form-select"
              >
                <option value="">All Status</option>
                <option value="Available -> Busy">Available to Busy</option>
                <option value="Busy -> Available">Busy to Available</option>
              </select>
            </div>

            <div className="form-group col-md-4 col-sm-6 col-12 p-2 d-flex align-items-end">
              <button
                type="button"
                onClick={handleClearFilters}
                className="btn w-100 waves-effect waves-light btn-rounded btn-outline-primary"
              >
                Clear
              </button>
            </div>
          </form>

          <div className="col-md-3 col-sm-6 col-12 p-2">
            <div className="dropdown export-btn">
              <button
                className="btn btn-outline-primary dropdown-toggle w-100"
                type="button"
                data-bs-toggle="dropdown"
              >
                Export Options
              </button>
              <ul className="dropdown-menu">
                <li>
                  <button className="dropdown-item" onClick={exportToPDF}>
                    Export to PDF
                  </button>
                </li>
                <li>
                  <button
                    className="dropdown-item"
                    onClick={() => onExport('resourceStatusHistory', 'csv')}
                  >
                    Export to CSV
                  </button>
                </li>
              </ul>
            </div>
          </div>
        </div>

        {/* Table */}
        <div className="py-3 px-0">
          <div className="table-responsive rounded-2 ros-table">
            <table className="table text-nowrap customize-table mb-0 align-middle resource-status-history-table">
              <thead className="text-dark fs-4">
                <tr>
                  <th><h6 className="fw-semibold mb-0">Resource Name</h6></th>
                  <th><h6 className="fw-semibold mb-0">Previous to New Status</h6></th>
                  <th><h6 className="fw-semibold mb-0">Date Changed</h6></th>
                  <th><h6 className="fw-semibold mb-0">Changed By</h6></th>
                </tr>
              </thead>
              <tbody>
                {loadingResourceStatus ? (
                  <tr>
                    <td colSpan={4} className="text-center">Loading...</td>
                  </tr>
                ) : paginatedData.length > 0 ? (
                  paginatedData.map((item) => (
                    <tr key={item.id}>
                      <td><p className="mb-0 fw-normal">{item.resource_name ?? 'N/A'}</p></td>
                      <td><p className="mb-0 fw-normal">{item.previous_and_new_status ?? 'N/A'}</p></td>
                      <td>
                        <p className="mb-0 fw-normal">
                          {item.created_at ? formatDateTime(item.created_at) : 'N/A'}
                        </p>
                      </td>
                      <td>
                        <p className="mb-0 fw-normal">
                          {`${item.user?.first_name ?? ''} ${item.user?.last_name ?? ''}`.trim() || 'N/A'}
                        </p>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={4} className="text-center">No data found</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Pagination */}
        {totalItems > 0 && (
          <Pagination
            total={totalItems}
            limit={limitResourceStatus}
            page={pageResourceStatus}
            onPageChange={setPageResourceStatus}
          />
        )}
      </div>
    </div>
  );
};

export default ResourceStatusHistory;